import React, { useState } from 'react';
import { Send, CheckCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewsletterSignup = ({ variant = 'default' }) => {
  const { language } = useLanguage();
  const sl = language === 'sl';
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const content = {
    sl: {
      title: 'Ostanite obveščeni',
      description: 'Prejemajte davčne novice, spremembe zakonodaje in praktične nasvete.',
      placeholder: 'vaš@email.si',
      button: 'Prijava',
      success: 'Hvala za prijavo! Pričakujte prvo sporočilo.',
      privacy: 'Vaše podatke varujemo. Odjava kadarkoli.',
      error: 'Prišlo je do napake. Poskusite znova.'
    },
    en: {
      title: 'Stay informed',
      description: 'Get tax news, regulatory updates and practical business tips for Slovenia.',
      placeholder: 'your@email.com',
      button: 'Subscribe',
      success: 'Thanks for subscribing! Expect our first email soon.',
      privacy: 'We protect your data. Unsubscribe anytime.',
      error: 'Something went wrong. Please try again.'
    }
  };

  const t = content[language] || content.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error(sl ? 'Vnesite veljaven email' : 'Enter a valid email');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, language })
      });

      if (response.ok) {
        setIsSubscribed(true);
        toast.success(t.success);
      } else {
        const error = await response.json();
        if (error.detail?.includes('already')) {
          toast.info(sl ? 'Ta email je že prijavljen' : 'This email is already subscribed');
          setIsSubscribed(true);
        } else {
          throw new Error(error.detail || 'Failed');
        }
      }
    } catch (error) {
      toast.error(t.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Compact variant for footer
  if (variant === 'compact') {
    if (isSubscribed) {
      return (
        <div className="flex items-center gap-2 text-[#C9A462]">
          <CheckCircle className="w-5 h-5" />
          <span className="text-sm">{sl ? 'Prijavljeni!' : 'Subscribed!'}</span>
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t.placeholder}
          className="flex-1 h-10 bg-[#1a1d21] border border-[#2A3036] text-[#F4EFE6] px-3 text-sm placeholder:text-[#7E786D] focus:outline-none focus:border-[#C9A462]"
          data-testid="newsletter-email-compact"
        />
        <button
          type="submit"
          disabled={isSubmitting}
          className="h-10 px-4 bg-[#C9A462] text-[#0E1114] font-semibold text-sm hover:bg-[#B8944F] transition-colors disabled:opacity-50 flex items-center gap-2"
          data-testid="newsletter-submit-compact"
        >
          {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    );
  }

  // Default full variant
  if (isSubscribed) {
    return (
      <div className="bg-[#0E1114] text-[#F4EFE6] p-8 lg:p-10 text-center" data-testid="newsletter-success">
        <CheckCircle className="w-12 h-12 text-[#C9A462] mx-auto mb-4" />
        <h3 className="font-serif text-2xl mb-2">{sl ? 'Prijava uspešna!' : 'Successfully subscribed!'}</h3>
        <p className="text-[#B9B3A7]">{t.success}</p>
      </div>
    );
  }

  return (
    <div className="bg-[#0E1114] text-[#F4EFE6] p-8 lg:p-12" data-testid="newsletter-section">
      <div className="max-w-xl mx-auto text-center">
        <span className="text-[13px] font-bold tracking-[2.5px] text-[#C9A462] block mb-3">
          {sl ? 'E-NOVICE' : 'NEWSLETTER'}
        </span>
        <h3 className="font-serif text-2xl lg:text-3xl mb-3">{t.title}</h3>
        <p className="text-[#B9B3A7] mb-6">{t.description}</p>
        
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.placeholder}
            className="flex-1 h-12 bg-[#15181C] border border-[#2A3036] text-[#F4EFE6] px-4 placeholder:text-[#7E786D] focus:outline-none focus:border-[#C9A462]"
            data-testid="newsletter-email"
          />
          <button
            type="submit"
            disabled={isSubmitting}
            className="h-12 px-6 bg-[#C9A462] text-[#0E1114] font-bold hover:bg-[#B8944F] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="newsletter-submit"
          >
            {isSubmitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                {t.button}
                <Send className="w-4 h-4" />
              </>
            )}
          </button>
        </form>
        
        <p className="text-xs text-[#7E786D] mt-4">{t.privacy}</p>
      </div>
    </div>
  );
};

export default NewsletterSignup;
